/**
 * HandwritingPad — a surface for writing maths by hand, not a drawing pad.
 *
 * The distinction matters. A learner proving a trig identity or working a long
 * division needs to write the way they write on paper: symbols stacked, terms
 * cancelled, a line crossed out and redone underneath. Typing that into a
 * textarea is a barrier that has nothing to do with whether they understand
 * the maths — which makes it a barrier the assessment should not contain.
 *
 * There is no handwriting recognition here and none is needed. The grader is
 * Claude, which reads the page the way a marker reads a script. We render the
 * ink to a PNG at submit time and send it.
 *
 * WHAT IS PERSISTED IS THE STROKES, NOT THE PIXELS. A page of working is a few
 * kilobytes as geometry and sixty-odd as an image, it re-renders at whatever
 * resolution the moment needs, and the learner can reopen a question and carry
 * on writing. `padToPng` derives the image when the grader needs one.
 */
import { useRef, useEffect, useState, useCallback } from "react";

export type Paper = "ruled" | "squared" | "plain";

type Pt = { x: number; y: number; p: number };
type Stroke = { pts: Pt[]; w: number; erase: boolean };
type PadDoc = { v: 1; h: number; paper: Paper; strokes: Stroke[] };

const LINE_H = 34;          // ruled spacing — sized for how teenagers actually write
const GRID = 20;
const MARGIN_X = 46;        // the red margin rule, as in an NSC answer book
const PEN_W = 2.2;
const ERASER_W = 24;
const START_H = 340;
const PAGE_STEP = 300;      // one press of "More space"
const INK = "#1b2534";
const TOUCH_INK_LEAD = 48;

const emptyDoc = (): PadDoc => ({ v: 1, h: START_H, paper: "ruled", strokes: [] });

function parseDoc(value: string): PadDoc {
  if (!value) return emptyDoc();
  try {
    const d = JSON.parse(value) as PadDoc;
    if (d && d.v === 1 && Array.isArray(d.strokes)) {
      return { ...emptyDoc(), ...d };
    }
  } catch {
    /* A corrupt pad should cost the learner an empty page, not a crash. */
  }
  return emptyDoc();
}

/* ── rendering ──────────────────────────────────────────────────────────── */

function drawPaper(ctx: CanvasRenderingContext2D, doc: PadDoc, w: number) {
  ctx.fillStyle = "#fffdfa";
  ctx.fillRect(0, 0, w, doc.h);

  if (doc.paper === "plain") return;

  ctx.lineWidth = 1;
  ctx.strokeStyle = "#dde5ee";
  ctx.beginPath();
  for (let y = LINE_H * 1.5; y < doc.h; y += LINE_H) {
    ctx.moveTo(0, y + 0.5);
    ctx.lineTo(w, y + 0.5);
  }
  if (doc.paper === "squared") {
    for (let x = GRID; x < w; x += GRID) {
      ctx.moveTo(x + 0.5, 0);
      ctx.lineTo(x + 0.5, doc.h);
    }
  }
  ctx.stroke();

  if (doc.paper === "ruled") {
    ctx.strokeStyle = "#f0d5d1";
    ctx.beginPath();
    ctx.moveTo(MARGIN_X + 0.5, 0);
    ctx.lineTo(MARGIN_X + 0.5, doc.h);
    ctx.stroke();
  }
}

/**
 * One stroke, smoothed.
 *
 * Joining raw pointer samples with `lineTo` produces visibly faceted letters —
 * handwriting drawn that way looks like a child's mouse scribble regardless of
 * how neatly it was written. Curving through the midpoints instead, with the
 * sample as the control point, is what makes ink read as ink.
 */
function drawStroke(ctx: CanvasRenderingContext2D, s: Stroke) {
  const pts = s.pts;
  if (!pts.length) return;

  ctx.globalCompositeOperation = s.erase ? "destination-out" : "source-over";
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.strokeStyle = INK;
  ctx.fillStyle = INK;

  if (pts.length === 1) {
    ctx.beginPath();
    ctx.arc(pts[0].x, pts[0].y, (s.w * (0.55 + 0.75 * pts[0].p)) / 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalCompositeOperation = "source-over";
    return;
  }

  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1];
    const b = pts[i];
    const from =
      i === 1 ? a : { x: (pts[i - 2].x + a.x) / 2, y: (pts[i - 2].y + a.y) / 2 };
    const to = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };

    ctx.lineWidth = s.erase ? s.w : s.w * (0.55 + 0.75 * b.p);
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.quadraticCurveTo(a.x, a.y, to.x, to.y);
    ctx.stroke();
  }
  ctx.globalCompositeOperation = "source-over";
}

function render(ctx: CanvasRenderingContext2D, doc: PadDoc, w: number) {
  ctx.clearRect(0, 0, w, doc.h);
  drawPaper(ctx, doc, w);
  for (const s of doc.strokes) drawStroke(ctx, s);
}

/* ── export ─────────────────────────────────────────────────────────────── */

export function padHasInk(value: string): boolean {
  return parseDoc(value).strokes.some((s) => !s.erase && s.pts.length > 0);
}

/**
 * Render a pad to a PNG data URL for the grader.
 *
 * Trimmed to the ink and drawn on plain white: the ruled lines help the learner
 * and tell the model nothing, and a page that is four-fifths empty is four-fifths
 * wasted tokens on every single submission.
 *
 * Returns null for an empty pad so callers can omit the field rather than send
 * a blank page and ask Claude to mark it.
 */
export function padToPng(
  value: string,
  width: number,
  opts: { maxWidth?: number; pad?: number } = {}
): string | null {
  const doc = parseDoc(value);
  if (!doc.strokes.length) return null;

  const padPx = opts.pad ?? 24;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const s of doc.strokes) {
    if (s.erase) continue;
    for (const p of s.pts) {
      const r = s.w;
      if (p.x - r < minX) minX = p.x - r;
      if (p.y - r < minY) minY = p.y - r;
      if (p.x + r > maxX) maxX = p.x + r;
      if (p.y + r > maxY) maxY = p.y + r;
    }
  }
  if (!isFinite(minX)) return null;

  minX = Math.max(0, minX - padPx);
  minY = Math.max(0, minY - padPx);
  maxX = Math.min(width, maxX + padPx);
  maxY = Math.min(doc.h, maxY + padPx);

  const cropW = Math.max(1, Math.ceil(maxX - minX));
  const cropH = Math.max(1, Math.ceil(maxY - minY));
  const scale = Math.min(1, (opts.maxWidth ?? 1400) / cropW);

  const c = document.createElement("canvas");
  c.width = Math.round(cropW * scale);
  c.height = Math.round(cropH * scale);
  const ctx = c.getContext("2d");
  if (!ctx) return null;

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, c.width, c.height);
  ctx.scale(scale, scale);
  ctx.translate(-minX, -minY);
  for (const s of doc.strokes) drawStroke(ctx, s);

  return c.toDataURL("image/png");
}

/* ── component ──────────────────────────────────────────────────────────── */

interface HandwritingPadProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Mirror the pen and eraser cursors for a left-handed writer. */
  leftHanded?: boolean;
}

export function HandwritingPad({
  value,
  onChange,
  disabled,
  leftHanded = false,
  placeholder = "Write your working here - use a stylus, finger, or mouse.",
}: HandwritingPadProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const docRef = useRef<PadDoc>(parseDoc(value));
  const liveRef = useRef<Stroke | null>(null);
  const widthRef = useRef(0);
  // Once a stylus has been seen, touches are the hand resting on the screen.
  const sawPenRef = useRef(false);
  const [tool, setTool] = useState<"pen" | "eraser">("pen");
  const [paper, setPaper] = useState<Paper>(docRef.current.paper);
  const [redo, setRedo] = useState<Stroke[]>([]);
  const [, forceTick] = useState(0);

  docRef.current = { ...parseDoc(value), paper };

  const paint = useCallback(() => {
    const cv = canvasRef.current;
    const ctx = cv?.getContext("2d");
    if (!cv || !ctx) return;
    render(ctx, docRef.current, widthRef.current);
    if (liveRef.current) drawStroke(ctx, liveRef.current);
  }, []);

  /* Size to the container at device resolution — a canvas sized in CSS pixels
     on a retina screen renders handwriting as a blurry smear. */
  const resize = useCallback(() => {
    const wrap = wrapRef.current;
    const cv = canvasRef.current;
    if (!wrap || !cv) return;
    const w = wrap.clientWidth;
    const h = docRef.current.h;
    const dpr = window.devicePixelRatio || 1;
    widthRef.current = w;
    cv.width = Math.round(w * dpr);
    cv.height = Math.round(h * dpr);
    cv.style.width = `${w}px`;
    cv.style.height = `${h}px`;
    const ctx = cv.getContext("2d");
    if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    paint();
  }, [paint]);

  useEffect(() => {
    resize();
    const ro = new ResizeObserver(resize);
    if (wrapRef.current) ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, [resize]);

  useEffect(() => { paint(); }, [value, paper, paint]);

  const commit = useCallback(
    (next: PadDoc) => {
      docRef.current = next;
      onChange(JSON.stringify(next));
    },
    [onChange]
  );

  const ptFrom = (e: React.PointerEvent | PointerEvent): Pt => {
    const r = canvasRef.current!.getBoundingClientRect();
    // A fingertip hides the contact point. Lift finger-written ink above it so
    // learners can see the line as they form it; stylus and mouse stay exact.
    const touchLead = e.pointerType === "touch" ? TOUCH_INK_LEAD : 0;
    return {
      x: e.clientX - r.left,
      y: Math.max(0, e.clientY - r.top - touchLead),
      // Mice and most touchscreens report 0 or a flat 0.5; only a stylus gives
      // a real curve, so anything else is pinned to a steady nib.
      p: e.pointerType === "pen" && e.pressure > 0 ? e.pressure : 0.5,
    };
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (disabled) return;
    if (e.pointerType === "pen") sawPenRef.current = true;
    if (e.pointerType === "touch" && sawPenRef.current) return; // palm
    e.preventDefault();
    canvasRef.current?.setPointerCapture(e.pointerId);

    // Styluses report the barrel/flip eraser as button 5 / buttons bit 32.
    const flipped = e.pointerType === "pen" && (e.buttons & 32) !== 0;
    const erase = tool === "eraser" || flipped;

    liveRef.current = {
      pts: [ptFrom(e)],
      w: erase ? ERASER_W : PEN_W,
      erase,
    };
    paint();
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!liveRef.current || disabled) return;
    e.preventDefault();
    // Coalesced events recover the samples the browser batched between frames.
    // Without them fast strokes lose their corners and every 7 becomes a 1.
    const native = e.nativeEvent as PointerEvent & {
      getCoalescedEvents?: () => PointerEvent[];
    };
    const raw = native.getCoalescedEvents?.() ?? [native];
    for (const ev of raw) liveRef.current.pts.push(ptFrom(ev));
    paint();
  };

  const endStroke = (e: React.PointerEvent) => {
    const live = liveRef.current;
    if (!live) return;
    canvasRef.current?.releasePointerCapture?.(e.pointerId);
    liveRef.current = null;
    if (!live.pts.length) return;
    commit({ ...docRef.current, strokes: [...docRef.current.strokes, live] });
    setRedo([]);
  };

  const undo = () => {
    const s = docRef.current.strokes;
    if (!s.length) return;
    setRedo((r) => [...r, s[s.length - 1]]);
    commit({ ...docRef.current, strokes: s.slice(0, -1) });
  };

  const redoLast = () => {
    if (!redo.length) return;
    const last = redo[redo.length - 1];
    setRedo((r) => r.slice(0, -1));
    commit({ ...docRef.current, strokes: [...docRef.current.strokes, last] });
  };

  const clear = () => {
    if (!docRef.current.strokes.length) return;
    commit({ ...docRef.current, strokes: [] });
    setRedo([]);
  };

  const addSpace = () => {
    commit({ ...docRef.current, h: docRef.current.h + PAGE_STEP });
    requestAnimationFrame(resize);
  };

  const setPaperMode = (p: Paper) => {
    setPaper(p);
    commit({ ...docRef.current, paper: p });
    forceTick((n) => n + 1);
  };

  const inkCount = docRef.current.strokes.length;

  return (
    <div className="hwpad">
      <div className="hwpad-tools">
        <div className="hwpad-group">
          <button
            type="button"
            className={`hwpad-btn ${tool === "pen" ? "active" : ""}`}
            onClick={() => setTool("pen")}
            disabled={disabled}
            aria-pressed={tool === "pen"}
          >
            ✎ Pen
          </button>
          <button
            type="button"
            className={`hwpad-btn ${tool === "eraser" ? "active" : ""}`}
            onClick={() => setTool("eraser")}
            disabled={disabled}
            aria-pressed={tool === "eraser"}
          >
            ⌫ Erase
          </button>
        </div>

        <div className="hwpad-group">
          <button type="button" className="hwpad-btn" onClick={undo}
                  disabled={disabled || !inkCount} aria-label="Undo last stroke">↶</button>
          <button type="button" className="hwpad-btn" onClick={redoLast}
                  disabled={disabled || !redo.length} aria-label="Redo stroke">↷</button>
        </div>

        <div className="hwpad-group hwpad-paper">
          {(["ruled", "squared", "plain"] as Paper[]).map((p) => (
            <button
              key={p}
              type="button"
              className={`hwpad-btn ${paper === p ? "active" : ""}`}
              onClick={() => setPaperMode(p)}
              disabled={disabled}
              aria-pressed={paper === p}
            >
              {p === "ruled" ? "Lined" : p === "squared" ? "Grid" : "Plain"}
            </button>
          ))}
        </div>

        <div className="hwpad-group hwpad-right">
          <button type="button" className="hwpad-btn" onClick={addSpace} disabled={disabled}>
            + More space
          </button>
          <button type="button" className="hwpad-btn hwpad-btn-danger" onClick={clear}
                  disabled={disabled || !inkCount}>
            Clear
          </button>
        </div>
      </div>

      <div className="hwpad-surface" ref={wrapRef}>
        <canvas
          ref={canvasRef}
          className={`hwpad-canvas ${tool === "eraser" ? "hwpad-canvas-eraser" : ""} ${leftHanded ? "hwpad-left" : ""}`}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endStroke}
          onPointerCancel={endStroke}
          onPointerLeave={endStroke}
        />
        {!inkCount && (
          <p className="hwpad-placeholder">
            {placeholder}
          </p>
        )}
      </div>
    </div>
  );
}
